import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { DateRange } from "./DateFilter";
import { useWorkspace } from "@/hooks/useWorkspace";

interface OpportunitiesChartProps {
  dateRange: DateRange;
}

const stageLabels: Record<string, string> = {
  prospecting: "Prospección", qualification: "Calificación", proposal: "Propuesta",
  negotiation: "Negociación", closed_won: "Ganada", closed_lost: "Perdida",
};

const stageColors: Record<string, string> = {
  prospecting: "hsl(215, 70%, 55%)", qualification: "hsl(187, 85%, 53%)", proposal: "hsl(280, 70%, 55%)",
  negotiation: "hsl(45, 90%, 55%)", closed_won: "hsl(142, 70%, 45%)", closed_lost: "hsl(0, 70%, 55%)",
};

interface StageData { name: string; value: number; stage: string; }

export function OpportunitiesChart({ dateRange }: OpportunitiesChartProps) {
  const [data, setData] = useState<StageData[]>([]);
  const [loading, setLoading] = useState(true);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [dateRange, workspaceId]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data: opportunities } = await supabase
        .from("opportunities")
        .select("stage, value")
        .eq("workspace_id", workspaceId)
        .gte("created_at", dateRange.from.toISOString())
        .lte("created_at", dateRange.to.toISOString());

      const stageCounts: Record<string, number> = {};
      opportunities?.forEach((opp) => {
        const stage = opp.stage || "prospecting";
        stageCounts[stage] = (stageCounts[stage] || 0) + 1;
      });

      const chartData = Object.entries(stageCounts).map(([stage, count]) => ({
        name: stageLabels[stage] || stage, value: count, stage,
      }));

      setData(chartData);
    } catch (error) {
      console.error("Error fetching opportunities by stage:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="chart-container">
        <div className="mb-4">
          <h3 className="font-display text-lg font-semibold text-foreground">Oportunidades por Etapa</h3>
          <p className="text-sm text-muted-foreground">Distribución del pipeline</p>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const hasData = data.length > 0;

  return (
    <div className="chart-container animate-slide-in-up">
      <div className="mb-4">
        <h3 className="font-display text-lg font-semibold text-foreground">Oportunidades por Etapa</h3>
        <p className="text-sm text-muted-foreground">Distribución del pipeline</p>
      </div>
      {!hasData ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">No hay oportunidades en este período</div>
      ) : (
        <div className="h-64 flex flex-col">
          <div className="flex-1 min-h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                  {data.map((entry, index) => (<Cell key={`cell-${index}`} fill={stageColors[entry.stage]} />))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(value: number) => [`${value} oportunidades`, ""]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {data.map((entry, index) => (
              <div key={`legend-${index}`} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stageColors[entry.stage] }} />
                <span className="text-xs text-foreground">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
